/**
 * JWT + API expiry helpers. Signin / SSO should pass expiresIn (seconds TTL) when available;
 * otherwise we derive absolute expiry from the JWT `exp` claim.
 */

/** Parse `expiresIn` query/body value as positive TTL seconds, or undefined. */
export function parseExpiresInParam(param) {
  if (param == null || param === '') return undefined;
  const n = typeof param === 'number' ? param : parseInt(String(param), 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function decodeJwtPayload(accessToken) {
  if (!accessToken || typeof accessToken !== 'string') return null;
  const parts = accessToken.split('.');
  if (parts.length < 2) return null;
  try {
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) base64 += '='.repeat(4 - pad);
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** Absolute expiry time in ms from JWT `exp`, or null if not a JWT / no claim. */
export function getJwtExpiryTimeMs(accessToken) {
  const payload = decodeJwtPayload(accessToken);
  if (!payload || typeof payload.exp !== 'number') return null;
  return payload.exp * 1000;
}

/**
 * Absolute wall expiry (ms). Prefer TTL from API/SSO (seconds from now); else JWT exp.
 */
export function resolveAccessTokenExpiryMs(accessToken, expiresInSeconds) {
  if (
    typeof expiresInSeconds === 'number' &&
    Number.isFinite(expiresInSeconds) &&
    expiresInSeconds > 0
  ) {
    return Date.now() + expiresInSeconds * 1000;
  }
  return getJwtExpiryTimeMs(accessToken);
}

/** Seconds until expiry, for BroadcastChannel / cross-tab sync. */
export function getBroadcastExpiresInSeconds(accessToken, expiresInSecondsHint) {
  if (
    typeof expiresInSecondsHint === 'number' &&
    Number.isFinite(expiresInSecondsHint) &&
    expiresInSecondsHint > 0
  ) {
    return Math.floor(expiresInSecondsHint);
  }
  const expMs = getJwtExpiryTimeMs(accessToken);
  if (expMs == null) return 0;
  return Math.max(0, Math.floor((expMs - Date.now()) / 1000));
}
